<template>
  <section class="ead-player">
    <titlebar
      :title-stack="[
        'Meus cursos',
        curso.resumo ? curso.resumo.nome : 'Curso',
      ]"
    />

    <div v-if="loading" class="player-loading">
      <b-loading :active="loading" :is-full-page="false" />
    </div>

    <template v-else-if="curso.resumo">
      <header class="course-header box">
        <div class="course-header__main">
          <router-link
            class="back-link"
            :to="{ name: 'ead-meus-cursos' }"
            aria-label="Voltar para meus cursos"
          >
            <b-icon icon="arrow-left" size="is-small" />
          </router-link>
          <div class="course-header__copy">
            <p class="eyebrow">Você está estudando</p>
            <h1>{{ curso.resumo.nome }}</h1>
            <p v-if="curso.resumo.descricao" class="course-description">
              {{ curso.resumo.descricao }}
            </p>
          </div>
        </div>

        <div class="course-progress" aria-label="Progresso do curso">
          <div class="course-progress__label">
            <span>Seu progresso</span>
            <strong>{{ progresso }}%</strong>
          </div>
          <ead-progresso :valor="progresso" />
          <span class="course-progress__detail">
            {{ aulasConcluidas }} de {{ aulas.length }} aulas concluídas
          </span>
        </div>
      </header>

      <button
        type="button"
        class="mobile-trail-button"
        aria-haspopup="dialog"
        :aria-expanded="trilhaMobileAberta ? 'true' : 'false'"
        @click="trilhaMobileAberta = true"
      >
        <span class="mobile-trail-button__icon">
          <b-icon icon="format-list-bulleted" />
        </span>
        <span>
          <small>Conteúdo do curso</small>
          <strong>{{ moduloAtual ? moduloAtual.nome : 'Ver trilha' }}</strong>
        </span>
        <b-icon icon="chevron-right" />
      </button>

      <div class="player-layout">
        <aside class="course-trail" aria-label="Conteúdo do curso">
          <div class="course-trail__heading">
            <div>
              <p class="eyebrow">Sua trilha</p>
              <h2>Conteúdo do curso</h2>
            </div>
            <span>{{ aulas.length }} aulas</span>
          </div>
          <div class="course-trail__scroll">
            <div
              v-for="(modulo, moduloIndex) in curso.modulos"
              :key="modulo.id"
              class="trail-module"
            >
              <p class="trail-module__title">
                <span>Módulo {{ moduloIndex + 1 }}</span>
                <strong>{{ modulo.nome }}</strong>
              </p>
              <button
                v-for="item in modulo.aulas"
                :key="item.id"
                type="button"
                class="trail-lesson"
                :class="{
                  'trail-lesson--active': aula.id === item.id,
                  'trail-lesson--done': item.concluida,
                }"
                :aria-current="aula.id === item.id ? 'true' : null"
                @click="abrir(item.id)"
              >
                <span class="trail-lesson__status">
                  <b-icon
                    :icon="item.concluida ? 'check' : 'play'"
                    size="is-small"
                  />
                </span>
                <span class="trail-lesson__copy">
                  <strong>{{ item.nome }}</strong>
                  <small v-if="item.duracaoEstimadaMinutos">
                    {{ item.duracaoEstimadaMinutos }} min
                  </small>
                </span>
              </button>
            </div>
            <router-link
              v-if="
                curso.possuiAvaliacaoFinal &&
                curso.avaliacao &&
                progresso === 100
              "
              class="trail-lesson trail-lesson--assessment"
              :to="{
                name: 'ead-avaliacao',
                params: { id: curso.avaliacao.id },
              }"
            >
              <span class="trail-lesson__status">
                <b-icon icon="clipboard-check-outline" size="is-small" />
              </span>
              <span class="trail-lesson__copy">
                <strong>Fazer avaliação</strong>
                <small>Etapa final do curso</small>
              </span>
            </router-link>
          </div>
        </aside>

        <main class="lesson-content">
          <div v-if="carregandoAula" class="lesson-loading">
            <b-loading :active="carregandoAula" :is-full-page="false" />
          </div>

          <b-message v-else-if="!aula.id" type="is-info">
            Selecione uma aula na trilha para começar.
          </b-message>

          <template v-else>
            <div class="lesson-heading">
              <div>
                <p class="lesson-position">
                  Aula {{ indiceAulaAtual + 1 }} de {{ aulas.length }}
                  <template v-if="moduloAtual">
                    · {{ moduloAtual.nome }}
                  </template>
                </p>
                <h2>{{ aula.nome }}</h2>
                <div class="lesson-meta">
                  <span v-if="aula.duracaoEstimadaMinutos">
                    <b-icon icon="clock-outline" size="is-small" />
                    {{ aula.duracaoEstimadaMinutos }} min
                  </span>
                  <span v-if="aula.concluida" class="lesson-status-done">
                    <b-icon icon="check-circle" size="is-small" />
                    Aula concluída
                  </span>
                </div>
              </div>
            </div>

            <div class="video-frame">
              <iframe
                :src="
                  'https://www.youtube-nocookie.com/embed/' +
                  aula.youtubeVideoId +
                  '?rel=0'
                "
                :title="`Vídeo da aula ${aula.nome}`"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowfullscreen
              />
            </div>

            <nav class="lesson-navigation" aria-label="Navegação entre aulas">
              <b-button
                icon-left="arrow-left"
                :disabled="!aulaAnterior || concluindo || salvando"
                @click="abrir(aulaAnterior.id)"
              >
                <span class="nav-label">Aula anterior</span>
              </b-button>
              <b-button
                icon-right="arrow-right"
                :disabled="!proximaAula || concluindo || salvando"
                @click="abrir(proximaAula.id)"
              >
                <span class="nav-label">Próxima aula</span>
              </b-button>
            </nav>

            <div v-if="aula.descricao" class="lesson-description">
              <h3>Sobre esta aula</h3>
              <p class="pre">{{ aula.descricao }}</p>
            </div>

            <section
              v-if="aula.perguntas.length"
              class="lesson-section activity-section"
            >
              <button
                type="button"
                class="section-heading section-heading--button"
                :aria-expanded="atividadeAberta ? 'true' : 'false'"
                @click="atividadeAberta = !atividadeAberta"
              >
                <span class="section-heading__icon">
                  <b-icon icon="comment-question-outline" />
                </span>
                <span class="section-heading__copy">
                  <strong>Atividade da aula</strong>
                  <small>Registre suas respostas antes de concluir.</small>
                </span>
                <b-tag rounded>{{ aula.perguntas.length }}</b-tag>
                <b-icon
                  :icon="atividadeAberta ? 'chevron-up' : 'chevron-down'"
                />
              </button>

              <div v-show="atividadeAberta" class="activity-content">
                <div
                  v-for="(pergunta, index) in aula.perguntas"
                  :key="pergunta.id"
                  class="question-card"
                >
                  <div class="question-card__heading">
                    <span>{{ index + 1 }}</span>
                    <div>
                      <h4>{{ pergunta.enunciado }}</h4>
                      <small v-if="pergunta.obrigatoria">
                        Resposta obrigatória
                      </small>
                    </div>
                  </div>

                  <b-input
                    v-if="pergunta.tipo === 'ABERTA'"
                    v-model="respostas[pergunta.id].respostaTexto"
                    type="textarea"
                    maxlength="4000"
                    placeholder="Digite sua resposta"
                  />
                  <div v-else class="question-options">
                    <div
                      v-for="alternativa in pergunta.alternativas"
                      :key="alternativa.id"
                      class="question-option"
                      :class="{
                        'question-option--selected':
                          respostas[pergunta.id].alternativaId === alternativa.id,
                      }"
                    >
                      <b-radio
                        v-model="respostas[pergunta.id].alternativaId"
                        :native-value="alternativa.id"
                      >
                        {{ alternativa.descricao }}
                      </b-radio>
                    </div>
                  </div>
                </div>

                <div class="activity-action">
                  <b-button
                    type="is-primary is-light"
                    icon-left="content-save-outline"
                    :loading="salvando"
                    :disabled="concluindo"
                    @click="salvar"
                  >
                    Salvar respostas
                  </b-button>
                </div>
              </div>
            </section>

            <section class="lesson-section doubts-section">
              <button
                type="button"
                class="section-heading section-heading--button"
                :aria-expanded="duvidasAbertas ? 'true' : 'false'"
                @click="duvidasAbertas = !duvidasAbertas"
              >
                <span class="section-heading__icon">
                  <b-icon icon="forum-outline" />
                </span>
                <span class="section-heading__copy">
                  <strong>Dúvidas e comentários</strong>
                  <small>Converse sobre o conteúdo desta aula.</small>
                </span>
                <b-tag v-if="comentarios.length" rounded>
                  {{ comentarios.length }}
                </b-tag>
                <b-icon :icon="duvidasAbertas ? 'chevron-up' : 'chevron-down'" />
              </button>

              <div v-show="duvidasAbertas" class="doubts-content">
                <div v-if="comentarios.length" class="comments-list">
                  <article
                    v-for="comentarioItem in comentarios"
                    :key="comentarioItem.id"
                    class="comment-item"
                  >
                    <span class="comment-avatar" aria-hidden="true">
                      {{ inicialAutor(comentarioItem.autor) }}
                    </span>
                    <div>
                      <div class="comment-item__meta">
                        <strong>{{ comentarioItem.autor }}</strong>
                        <time :datetime="comentarioItem.data">
                          {{ formatarDataComentario(comentarioItem.data) }}
                        </time>
                      </div>
                      <p class="pre">{{ comentarioItem.comentario }}</p>
                    </div>
                  </article>
                </div>
                <p v-else class="empty-comments">
                  Ainda não há comentários. Se precisar, envie a primeira dúvida.
                </p>

                <b-field label="Escreva uma dúvida ou comentário">
                  <b-input
                    v-model="comentario"
                    type="textarea"
                    maxlength="4000"
                    placeholder="Conte onde você precisa de ajuda"
                  />
                </b-field>
                <div class="comment-action">
                  <b-button
                    type="is-primary"
                    icon-left="send"
                    :loading="comentando"
                    :disabled="!comentario.trim()"
                    @click="comentar"
                  >
                    Enviar comentário
                  </b-button>
                </div>
              </div>
            </section>

            <div class="lesson-actions">
              <b-button
                type="is-success"
                icon-left="check-circle-outline"
                :loading="concluindo"
                :disabled="salvando"
                @click="concluir"
              >
                {{ aula.concluida ? 'Salvar e continuar' : 'Concluir aula' }}
              </b-button>
            </div>
          </template>
        </main>
      </div>
    </template>

    <div
      v-if="trilhaMobileAberta"
      class="mobile-trail-overlay"
      role="presentation"
      @click.self="trilhaMobileAberta = false"
    >
      <aside
        class="mobile-trail"
        role="dialog"
        aria-modal="true"
        aria-label="Conteúdo do curso"
      >
        <div class="mobile-trail__heading">
          <div>
            <p class="eyebrow">Sua trilha</p>
            <h2>Conteúdo do curso</h2>
          </div>
          <button
            type="button"
            class="close-trail"
            aria-label="Fechar conteúdo do curso"
            @click="trilhaMobileAberta = false"
          >
            <b-icon icon="close" />
          </button>
        </div>
        <div class="mobile-trail__progress">
          <span>{{ progresso }}% concluído</span>
          <ead-progresso :valor="progresso" />
        </div>
        <div class="mobile-trail__content">
          <div
            v-for="(modulo, moduloIndex) in curso.modulos"
            :key="modulo.id"
            class="trail-module"
          >
            <p class="trail-module__title">
              <span>Módulo {{ moduloIndex + 1 }}</span>
              <strong>{{ modulo.nome }}</strong>
            </p>
            <button
              v-for="item in modulo.aulas"
              :key="item.id"
              type="button"
              class="trail-lesson"
              :class="{
                'trail-lesson--active': aula.id === item.id,
                'trail-lesson--done': item.concluida,
              }"
              @click="abrir(item.id)"
            >
              <span class="trail-lesson__status">
                <b-icon
                  :icon="item.concluida ? 'check' : 'play'"
                  size="is-small"
                />
              </span>
              <span class="trail-lesson__copy">
                <strong>{{ item.nome }}</strong>
                <small v-if="item.duracaoEstimadaMinutos">
                  {{ item.duracaoEstimadaMinutos }} min
                </small>
              </span>
            </button>
          </div>
          <router-link
            v-if="
              curso.possuiAvaliacaoFinal &&
              curso.avaliacao &&
              progresso === 100
            "
            class="trail-lesson trail-lesson--assessment"
            :to="{
              name: 'ead-avaliacao',
              params: { id: curso.avaliacao.id },
            }"
            @click.native="trilhaMobileAberta = false"
          >
            <span class="trail-lesson__status">
              <b-icon icon="clipboard-check-outline" size="is-small" />
            </span>
            <span class="trail-lesson__copy">
              <strong>Fazer avaliação</strong>
              <small>Etapa final do curso</small>
            </span>
          </router-link>
        </div>
      </aside>
    </div>
  </section>
</template>

<script>
import http from '@/http'
import titlebar from '@/components/titlebar.vue'
import EadProgresso from '@/components/ead/ead-progresso.vue'

export default {
  name: 'EadCursoPlayer',
  components: { titlebar, EadProgresso },
  data: () => ({
    curso: {},
    aula: {},
    respostas: {},
    comentarios: [],
    comentario: '',
    loading: false,
    carregandoAula: false,
    salvando: false,
    concluindo: false,
    comentando: false,
    trilhaMobileAberta: false,
    atividadeAberta: false,
    duvidasAbertas: false,
  }),
  computed: {
    aulas() {
      if (!this.curso.modulos) return []
      return this.curso.modulos.reduce(
        (lista, modulo) =>
          lista.concat(
            (modulo.aulas || []).map((aula) => ({
              ...aula,
              moduloId: modulo.id,
              moduloNome: modulo.nome,
            }))
          ),
        []
      )
    },
    progresso() {
      if (!this.curso.resumo) return 0
      return Math.round(Number(this.curso.resumo.progresso) || 0)
    },
    aulasConcluidas() {
      return this.aulas.filter((item) => item.concluida).length
    },
    indiceAulaAtual() {
      return this.aulas.findIndex((item) => item.id === this.aula.id)
    },
    aulaAnterior() {
      return this.indiceAulaAtual > 0
        ? this.aulas[this.indiceAulaAtual - 1]
        : null
    },
    proximaAula() {
      return this.indiceAulaAtual >= 0 &&
        this.indiceAulaAtual < this.aulas.length - 1
        ? this.aulas[this.indiceAulaAtual + 1]
        : null
    },
    moduloAtual() {
      if (!this.aula.id || !this.curso.modulos) return null
      return this.curso.modulos.find((modulo) =>
        (modulo.aulas || []).some((item) => item.id === this.aula.id)
      )
    },
  },
  mounted() {
    this.carregar()
    document.addEventListener('keydown', this.fecharTrilhaComEscape)
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.fecharTrilhaComEscape)
    document.body.classList.remove('ead-trail-open')
  },
  methods: {
    carregar(aulaPreferidaId) {
      this.loading = true
      return http
        .get(`/api/ead-aprendizagem/cursos/${this.$route.params.id}`)
        .then((r) => {
          this.curso = r.data
          const aulas = this.aulas
          const preferida = aulas.find((item) => item.id === aulaPreferidaId)
          const primeiraPendente = aulas.find((item) => !item.concluida)
          const inicial = preferida || primeiraPendente || aulas[0]
          if (inicial) return this.abrir(inicial.id)
          this.aula = {}
          return null
        })
        .finally(() => {
          this.loading = false
        })
    },
    abrir(id) {
      if (!id || this.carregandoAula) return Promise.resolve()
      this.trilhaMobileAberta = false
      document.body.classList.remove('ead-trail-open')
      this.carregandoAula = true
      return http
        .get(
          `/api/ead-aprendizagem/aulas/${id}?liberacaoId=${this.$route.params.id}`
        )
        .then((r) => {
          this.aula = r.data
          this.respostas = {}
          ;(r.data.perguntas || []).forEach((pergunta) =>
            this.$set(this.respostas, pergunta.id, {
              perguntaId: pergunta.id,
              respostaTexto:
                pergunta.resposta && pergunta.resposta.respostaTexto,
              alternativaId:
                pergunta.resposta && pergunta.resposta.alternativaId,
            })
          )
          this.atividadeAberta = false
          this.duvidasAbertas = false
          return this.carregarComentarios()
        })
        .finally(() => {
          this.carregandoAula = false
        })
    },
    payload() {
      return {
        liberacaoId: Number(this.$route.params.id),
        respostas: Object.values(this.respostas),
      }
    },
    salvar(exibirMensagem = true) {
      this.salvando = true
      return http
        .put(
          `/api/ead-aprendizagem/aulas/${this.aula.id}/respostas`,
          this.payload()
        )
        .then(() => {
          if (exibirMensagem) {
            this.$buefy.toast.open({
              message: 'Respostas salvas.',
              type: 'is-success',
            })
          }
        })
        .finally(() => {
          this.salvando = false
        })
    },
    concluir() {
      if (this.concluindo) return
      this.concluindo = true
      const proximaAulaId = this.proximaAula && this.proximaAula.id
      this.salvar(false)
        .then(() =>
          http.post(
            `/api/ead-aprendizagem/aulas/${this.aula.id}/concluir?liberacaoId=${this.$route.params.id}`
          )
        )
        .then(() => {
          this.$buefy.toast.open({
            message: proximaAulaId
              ? 'Aula concluída. Vamos para a próxima!'
              : 'Aula concluída.',
            type: 'is-success',
          })
          return this.carregar(proximaAulaId || this.aula.id)
        })
        .finally(() => {
          this.concluindo = false
        })
    },
    carregarComentarios() {
      return http
        .get(
          `/api/ead-comentarios/aulas/${this.aula.id}?liberacaoId=${this.$route.params.id}`
        )
        .then((r) => {
          this.comentarios = r.data || []
        })
    },
    comentar() {
      if (!this.comentario.trim() || this.comentando) return
      this.comentando = true
      http
        .post(`/api/ead-comentarios/aulas/${this.aula.id}`, {
          liberacaoId: Number(this.$route.params.id),
          comentario: this.comentario.trim(),
        })
        .then(() => {
          this.comentario = ''
          this.$buefy.toast.open({
            message: 'Comentário enviado.',
            type: 'is-success',
          })
          return this.carregarComentarios()
        })
        .finally(() => {
          this.comentando = false
        })
    },
    formatarDataComentario(data) {
      if (!data) return ''
      return new Date(data).toLocaleString('pt-BR', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    inicialAutor(autor) {
      return (autor || '?').trim().charAt(0).toLocaleUpperCase('pt-BR')
    },
    fecharTrilhaComEscape(evento) {
      if (evento.key === 'Escape') this.trilhaMobileAberta = false
    },
  },
  watch: {
    trilhaMobileAberta(aberta) {
      document.body.classList.toggle('ead-trail-open', aberta)
    },
  },
}
</script>

<style scoped>
.ead-player {
  color: #363636;
  min-height: 100%;
  position: relative;
}

.player-loading,
.lesson-loading {
  min-height: 320px;
  position: relative;
}

.course-header {
  align-items: center;
  background: linear-gradient(135deg, #ffffff 0%, #f3fbf9 100%);
  border: 1px solid #e5f1ef;
  border-left: 4px solid #00b89c;
  display: flex;
  gap: 2rem;
  justify-content: space-between;
  margin-bottom: 1.25rem;
  padding: 1.25rem 1.5rem;
}

.course-header__main,
.course-progress__label,
.lesson-meta,
.section-heading,
.question-card__heading,
.comment-item__meta {
  align-items: center;
  display: flex;
}

.course-header__main {
  gap: 1rem;
  min-width: 0;
}

.back-link,
.section-heading__icon,
.mobile-trail-button__icon,
.comment-avatar {
  align-items: center;
  display: inline-flex;
  flex: 0 0 auto;
  justify-content: center;
}

.back-link {
  background: #ffffff;
  border: 1px solid #dbdbdb;
  border-radius: 50%;
  color: #4a4a4a;
  height: 2.5rem;
  transition: border-color 0.2s ease, color 0.2s ease;
  width: 2.5rem;
}

.back-link:hover {
  border-color: #00b89c;
  color: #008f79;
}

.course-header__copy {
  min-width: 0;
}

.eyebrow,
.lesson-position,
.trail-module__title span {
  color: #008f79;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.course-header h1 {
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1.25;
  margin: 0.15rem 0 0;
}

.course-description {
  color: #7a7a7a;
  display: -webkit-box;
  font-size: 0.85rem;
  line-height: 1.45;
  margin-top: 0.35rem;
  max-width: 680px;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.course-progress {
  flex: 0 0 260px;
}

.course-progress__label {
  font-size: 0.78rem;
  justify-content: space-between;
  margin-bottom: 0.35rem;
}

.course-progress__label strong {
  color: #008f79;
}

.course-progress__detail {
  color: #7a7a7a;
  display: block;
  font-size: 0.7rem;
  margin-top: 0.3rem;
  text-align: right;
}

.course-progress ::v-deep .progress-wrapper,
.course-progress ::v-deep .progress,
.mobile-trail__progress ::v-deep .progress-wrapper,
.mobile-trail__progress ::v-deep .progress {
  margin-bottom: 0;
}

.player-layout {
  align-items: flex-start;
  display: grid;
  gap: 1.5rem;
  grid-template-columns: minmax(250px, 300px) minmax(0, 1fr);
}

.course-trail {
  background: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 9px;
  box-shadow: 0 3px 12px rgba(10, 10, 10, 0.05);
  max-height: calc(100vh - 7rem);
  overflow: hidden;
  position: sticky;
  top: 1rem;
}

.course-trail__heading,
.mobile-trail__heading {
  align-items: center;
  border-bottom: 1px solid #ededed;
  display: flex;
  justify-content: space-between;
  padding: 1rem;
}

.course-trail__heading h2,
.mobile-trail__heading h2 {
  font-size: 1rem;
  font-weight: 700;
}

.course-trail__heading > span {
  background: #f5f5f5;
  border-radius: 999px;
  color: #7a7a7a;
  font-size: 0.68rem;
  padding: 0.25rem 0.55rem;
}

.course-trail__scroll {
  max-height: calc(100vh - 12.5rem);
  overflow-y: auto;
  overscroll-behavior: contain;
}

.trail-module {
  padding: 1rem 0.75rem 0.35rem;
}

.trail-module + .trail-module {
  border-top: 1px solid #f0f0f0;
}

.trail-module__title {
  line-height: 1.25;
  margin: 0 0.5rem 0.55rem;
}

.trail-module__title span,
.trail-module__title strong {
  display: block;
}

.trail-module__title strong {
  font-size: 0.82rem;
  margin-top: 0.2rem;
}

.trail-lesson {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 7px;
  color: #4a4a4a;
  cursor: pointer;
  display: flex;
  font-family: inherit;
  gap: 0.65rem;
  margin-bottom: 0.25rem;
  padding: 0.65rem;
  text-align: left;
  transition: background-color 0.15s ease, color 0.15s ease;
  width: 100%;
}

.trail-lesson:hover {
  background: #f7f7f7;
}

.trail-lesson--active {
  background: #edf9f7;
  color: #007a67;
}

.trail-lesson--active:hover {
  background: #e7f6f3;
}

.trail-lesson__status {
  align-items: center;
  border: 1px solid #dbdbdb;
  border-radius: 50%;
  color: #7a7a7a;
  display: inline-flex;
  flex: 0 0 auto;
  height: 1.75rem;
  justify-content: center;
  width: 1.75rem;
}

.trail-lesson--active .trail-lesson__status {
  background: #00b89c;
  border-color: #00b89c;
  color: #ffffff;
}

.trail-lesson--done:not(.trail-lesson--active) .trail-lesson__status {
  background: #effaf5;
  border-color: #48c78e;
  color: #257953;
}

.trail-lesson--assessment {
  background: #fffbeb;
  border: 1px solid #ffdd57;
  color: #7a5b00;
  margin: 0.65rem 0.75rem 1rem;
  width: calc(100% - 1.5rem);
}

.trail-lesson--assessment:hover {
  background: #fff3c4;
  color: #594300;
}

.trail-lesson--assessment .trail-lesson__status {
  background: #ffdd57;
  border-color: #ffdd57;
  color: #594300;
}

.trail-lesson__copy {
  min-width: 0;
}

.trail-lesson__copy strong,
.trail-lesson__copy small {
  display: block;
}

.trail-lesson__copy strong {
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1.3;
}

.trail-lesson__copy small {
  color: #8a8a8a;
  font-size: 0.65rem;
  margin-top: 0.18rem;
}

.lesson-content {
  min-width: 0;
}

.lesson-heading {
  align-items: flex-start;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.lesson-heading h2 {
  color: #242424;
  font-size: 1.65rem;
  font-weight: 700;
  line-height: 1.25;
  margin: 0.2rem 0 0.4rem;
}

.lesson-meta {
  color: #7a7a7a;
  flex-wrap: wrap;
  font-size: 0.75rem;
  gap: 0.85rem;
}

.lesson-meta span {
  align-items: center;
  display: inline-flex;
  gap: 0.25rem;
}

.lesson-meta .lesson-status-done {
  color: #257953;
  font-weight: 600;
}

.video-frame {
  background: #111111;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(10, 10, 10, 0.14);
  margin-bottom: 1.5rem;
  overflow: hidden;
  position: relative;
}

.video-frame::before {
  content: '';
  display: block;
  padding-bottom: 56.25%;
}

.video-frame iframe {
  border: 0;
  height: 100%;
  inset: 0;
  position: absolute;
  width: 100%;
}

.lesson-description,
.lesson-section {
  background: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 9px;
  margin-bottom: 1.25rem;
  padding: 1.25rem;
}

.lesson-description h3 {
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 0.55rem;
}

.lesson-description p {
  color: #5c5c5c;
  font-size: 0.9rem;
  line-height: 1.65;
}

.section-heading {
  gap: 0.75rem;
  margin-bottom: 1.25rem;
}

.section-heading__icon,
.mobile-trail-button__icon {
  background: rgba(0, 184, 156, 0.1);
  border-radius: 50%;
  color: #008f79;
  height: 2.5rem;
  width: 2.5rem;
}

.section-heading > div,
.section-heading__copy {
  flex: 1;
  min-width: 0;
}

.section-heading h3,
.section-heading__copy strong {
  display: block;
  font-size: 1rem;
  font-weight: 700;
}

.section-heading p,
.section-heading__copy small {
  color: #7a7a7a;
  display: block;
  font-size: 0.75rem;
}

.question-card {
  background: #fafafa;
  border: 1px solid #ededed;
  border-radius: 8px;
  padding: 1rem;
}

.question-card + .question-card {
  margin-top: 0.85rem;
}

.question-card__heading {
  align-items: flex-start;
  gap: 0.7rem;
  margin-bottom: 0.85rem;
}

.question-card__heading > span {
  align-items: center;
  background: #00b89c;
  border-radius: 50%;
  color: #ffffff;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 0.72rem;
  font-weight: 700;
  height: 1.65rem;
  justify-content: center;
  width: 1.65rem;
}

.question-card__heading h4 {
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.4;
}

.question-card__heading small {
  color: #d64561;
  display: block;
  font-size: 0.68rem;
  margin-top: 0.2rem;
}

.question-options {
  display: grid;
  gap: 0.5rem;
}

.question-option {
  background: #ffffff;
  border: 1px solid #dbdbdb;
  border-radius: 7px;
  cursor: pointer;
  display: block;
  padding: 0.65rem 0.75rem;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.question-option:hover,
.question-option--selected {
  background: #effaf8;
  border-color: #00b89c;
}

.question-option ::v-deep .b-radio.radio {
  display: flex;
  margin: 0;
  width: 100%;
}

.activity-action,
.lesson-actions,
.lesson-navigation,
.comment-action {
  display: flex;
}

.activity-action {
  justify-content: flex-end;
  margin-top: 1rem;
}

.lesson-actions {
  background: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 9px;
  gap: 0.65rem;
  justify-content: flex-end;
  margin-bottom: 0.75rem;
  padding: 0.85rem;
}

.lesson-navigation {
  justify-content: space-between;
  margin-bottom: 1.5rem;
}

.activity-section,
.doubts-section {
  padding: 0;
}

.section-heading--button {
  background: transparent;
  border: 0;
  cursor: pointer;
  font-family: inherit;
  margin: 0;
  padding: 1.1rem 1.25rem;
  text-align: left;
  width: 100%;
}

.activity-content,
.doubts-content {
  border-top: 1px solid #ededed;
  padding: 1.25rem;
}

.comments-list {
  margin-bottom: 1.25rem;
}

.comment-item {
  align-items: flex-start;
  display: grid;
  gap: 0.7rem;
  grid-template-columns: auto minmax(0, 1fr);
  padding: 0.85rem 0;
}

.comment-item + .comment-item {
  border-top: 1px solid #f0f0f0;
}

.comment-avatar {
  background: #eef6f5;
  border-radius: 50%;
  color: #008f79;
  font-size: 0.78rem;
  font-weight: 700;
  height: 2.25rem;
  width: 2.25rem;
}

.comment-item__meta {
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: space-between;
  margin-bottom: 0.2rem;
}

.comment-item__meta strong {
  font-size: 0.82rem;
}

.comment-item__meta time {
  color: #8a8a8a;
  font-size: 0.68rem;
}

.comment-item p,
.empty-comments {
  color: #5c5c5c;
  font-size: 0.82rem;
  line-height: 1.55;
}

.empty-comments {
  background: #fafafa;
  border-radius: 7px;
  margin-bottom: 1rem;
  padding: 0.85rem;
  text-align: center;
}

.comment-action {
  justify-content: flex-end;
}

.pre {
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

.mobile-trail-button,
.mobile-trail-overlay {
  display: none;
}

@media screen and (max-width: 1100px) {
  .player-layout {
    gap: 1rem;
    grid-template-columns: minmax(230px, 260px) minmax(0, 1fr);
  }

  .course-header {
    align-items: stretch;
    flex-direction: column;
    gap: 1rem;
  }

  .course-progress {
    flex-basis: auto;
  }
}

@media screen and (max-width: 768px) {
  .course-header {
    border-radius: 0;
    margin: 0 -0.75rem 0.85rem;
    padding: 1rem;
  }

  .course-header__main {
    align-items: flex-start;
    gap: 0.65rem;
  }

  .back-link {
    height: 2.25rem;
    width: 2.25rem;
  }

  .course-header h1 {
    font-size: 1.15rem;
  }

  .course-description {
    -webkit-line-clamp: 1;
  }

  .course-progress__detail {
    text-align: left;
  }

  .player-layout {
    display: block;
  }

  .course-trail {
    display: none;
  }

  .mobile-trail-button {
    align-items: center;
    background: #ffffff;
    border: 1px solid #e5e5e5;
    border-radius: 8px;
    color: #363636;
    display: grid;
    font-family: inherit;
    gap: 0.7rem;
    grid-template-columns: auto minmax(0, 1fr) auto;
    margin-bottom: 1rem;
    padding: 0.7rem;
    text-align: left;
    width: 100%;
  }

  .mobile-trail-button small,
  .mobile-trail-button strong {
    display: block;
  }

  .mobile-trail-button small {
    color: #7a7a7a;
    font-size: 0.65rem;
  }

  .mobile-trail-button strong {
    font-size: 0.82rem;
    line-height: 1.25;
  }

  .mobile-trail-button__icon {
    height: 2.35rem;
    width: 2.35rem;
  }

  .lesson-heading {
    flex-direction: column;
  }

  .lesson-heading h2 {
    font-size: 1.35rem;
  }

  .video-frame {
    border-radius: 6px;
    margin-left: -0.75rem;
    margin-right: -0.75rem;
  }

  .lesson-description,
  .lesson-section {
    padding: 1rem;
  }

  .lesson-actions {
    bottom: 0;
    box-shadow: 0 -4px 16px rgba(10, 10, 10, 0.12);
    flex-direction: column-reverse;
    margin: 0 -0.75rem 0.75rem;
    padding: 0.75rem;
    position: sticky;
    z-index: 5;
  }

  .lesson-actions .button {
    width: 100%;
  }

  .activity-action .button {
    width: 100%;
  }

  .lesson-navigation .button {
    min-width: 3rem;
  }

  .nav-label {
    display: none;
  }

  .section-heading--button,
  .activity-content,
  .doubts-content {
    padding: 1rem;
  }

  .mobile-trail-overlay {
    background: rgba(10, 10, 10, 0.52);
    display: flex;
    inset: 0;
    justify-content: flex-end;
    position: fixed;
    z-index: 1000;
  }

  .mobile-trail {
    background: #ffffff;
    box-shadow: -8px 0 24px rgba(10, 10, 10, 0.18);
    display: flex;
    flex-direction: column;
    height: 100%;
    max-width: 360px;
    width: min(90vw, 360px);
  }

  .close-trail {
    align-items: center;
    background: #f5f5f5;
    border: 0;
    border-radius: 50%;
    color: #4a4a4a;
    display: inline-flex;
    height: 2.25rem;
    justify-content: center;
    width: 2.25rem;
  }

  .mobile-trail__progress {
    background: #fafafa;
    border-bottom: 1px solid #ededed;
    padding: 0.75rem 1rem;
  }

  .mobile-trail__progress > span {
    display: block;
    font-size: 0.7rem;
    font-weight: 600;
    margin-bottom: 0.3rem;
  }

  .mobile-trail__content {
    flex: 1;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding-bottom: max(1rem, env(safe-area-inset-bottom));
  }
}

@media screen and (max-width: 420px) {
  .course-header__copy .eyebrow,
  .course-description {
    display: none;
  }

  .question-card {
    padding: 0.85rem;
  }

  .section-heading__icon {
    height: 2.2rem;
    width: 2.2rem;
  }

  .comment-item__meta {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.1rem;
  }
}
</style>

<style>
body.ead-trail-open {
  overflow: hidden;
}
</style>
